import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { FaBriefcase, FaUserTie, FaUniversity, FaBuilding, FaHardHat } from 'react-icons/fa';
import SectionHeader from './_primitives/SectionHeader';
import DemoPopover from './DemoPopover';

const AUDIENCES = [
  {
    id:       'seekers',
    icon:     <FaBriefcase />,
    label:    'Job Seekers',
    headline: 'Find your next role — in any industry.',
    body:     'AI-powered matching across white-collar, blue-collar and government jobs. Build an ATS-ready resume, set smart alerts, and apply with one click.',
    features: ['AI Job Matching', 'Resume Builder', 'Smart Alerts', 'Govt Job Notifications', 'One-Click Apply'],
    cta:      'Start job hunting →',
    accent:   'text-brand-purple',
    dotBg:    'bg-brand-purple',
    bg:       'bg-secondary-50',
  },
  {
    id:       'recruiters',
    icon:     <FaUserTie />,
    label:    'Recruiters',
    headline: 'Hire faster with an AI-powered dashboard.',
    body:     'Post jobs, manage pipelines, filter candidates with AI, and get hire-ready analytics — all from one recruiter workspace.',
    features: ['Job Posting', 'Pipeline Tracker', 'AI Candidate Filtering', 'Analytics', 'Team Collaboration'],
    cta:      'Post your first job free →',
    ctaHref:  'https://recruiter.jobxbharat.com/',
    accent:   'text-brand-saffron',
    dotBg:    'bg-brand-saffron',
    bg:       'bg-orange-50',
  },
  {
    id:       'colleges',
    icon:     <FaUniversity />,
    label:    'Colleges',
    headline: 'Replace spreadsheets with a placement engine.',
    body:     'Our Placement ERP handles student profiles, campus drives, recruiter onboarding, eligibility filtering, and placement analytics — all in one tool.',
    features: ['Student Management', 'Campus Drive Co-ord', 'Recruiter Onboarding', 'Eligibility Filters', 'Placement Analytics'],
    cta:      'Book a college demo →',
    accent:   'text-brand-green',
    dotBg:    'bg-brand-green',
    bg:       'bg-green-50',
  },
  {
    id:       'businesses',
    icon:     <FaBuilding />,
    label:    'Businesses',
    headline: 'Hire skilled workers by skill and locality.',
    body:     'Need individual workers, teams, or contract labour? Discover candidates from your area — perfect for MSMEs, local businesses, and enterprises.',
    features: ['Locality-Based Search', 'Bulk Hiring', 'On-Demand Workforce', 'Contract Labour', 'Worker Profiles'],
    cta:      'Find workers near you →',
    accent:   'text-amber-600',
    dotBg:    'bg-amber-500',
    bg:       'bg-amber-50',
  },
  {
    id:       'contractors',
    icon:     <FaHardHat />,
    label:    'Contractors',
    headline: 'Get digital visibility for your workforce.',
    body:     'List your skilled team, take contract listings, and connect with businesses that need reliable workers fast.',
    features: ['Worker Aggregation', 'Contract Listings', 'Digital Profile', 'Locality Presence', 'Direct Leads'],
    cta:      'Register your workforce →',
    accent:   'text-rose-600',
    dotBg:    'bg-rose-500',
    bg:       'bg-rose-50',
  },
];

export default function AudienceSwitcher({ onStartSearching }) {
  const [active, setActive] = useState('seekers');
  const [showDemo, setShowDemo] = useState(false);
  const current = AUDIENCES.find(a => a.id === active);

  return (
    <>
    <section id="seekers" className="py-14 md:py-24 px-4 sm:px-6 bg-paper border-t border-gray-100">
      <div className="max-w-7xl mx-auto">
        <SectionHeader
          eyebrow={<>Who is Jobx<span className="text-brand-saffron">Bharat</span> for?</>}
          title="Built for everyone in India's workforce"
        />

        {/* Tab pills */}
        <div className="flex flex-wrap justify-center gap-3 mb-12">
          {AUDIENCES.map(a => (
            <button
              key={a.id}
              onClick={() => setActive(a.id)}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-full
                          text-sm font-bold border transition-all duration-200
                          ${active === a.id
                            ? 'bg-ink-900 text-white border-ink-900 shadow-md'
                            : 'bg-white text-ink-500 border-gray-200 hover:border-ink-900'}`}
            >
              <span className="text-base">{a.icon}</span>
              {a.label}
            </button>
          ))}
        </div>

        {/* Content panel */}
        <AnimatePresence mode="wait">
          <motion.div
            key={active}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.25 }}
            className={`rounded-3xl ${current.bg} border border-gray-100 p-6 sm:p-8 md:p-12
                        flex flex-col md:flex-row gap-6 md:gap-10 items-start`}
          >
            {/* Left copy */}
            <div className="flex-1">
              <h3 className="font-display font-black text-3xl md:text-4xl text-ink-900 mb-4">
                {current.headline}
              </h3>
              <p className="text-ink-500 text-lg leading-relaxed mb-8">{current.body}</p>
              {current.ctaHref ? (
                <a
                  href={current.ctaHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`inline-flex items-center gap-2 font-black text-sm ${current.accent}
                              hover:opacity-80 transition-opacity`}
                >
                  {current.cta}
                </a>
              ) : (
                <button
                  onClick={current.id === 'colleges' ? () => setShowDemo(true) : onStartSearching}
                  className={`inline-flex items-center gap-2 font-black text-sm ${current.accent}
                              hover:opacity-80 transition-opacity`}
                >
                  {current.cta}
                </button>
              )}
            </div>

            {/* Right feature chips */}
            <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-3">
              {current.features.map(f => (
                <div key={f}
                     className="flex items-center gap-3 bg-white rounded-2xl px-4 py-3
                                border border-gray-100 shadow-sm">
                  <span className={`w-2 h-2 rounded-full shrink-0 ${current.dotBg}`} />
                  <span className="text-sm font-semibold text-ink-700">{f}</span>
                </div>
              ))}
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </section>

    <DemoPopover open={showDemo} onClose={() => setShowDemo(false)} />
    </>
  );
}
